import Vue from 'vue'
import applogger from '@applogger'
import { PRODUCT_ID_QUERY } from '../queries/productIDQuery'
import fieldMapFactories from '../utils/fieldMapFactories'
import applyFieldMapper from '../utils/applyFieldMapper'
import createVariableMap from '../utils/createVariableMap'
import createTaxonomyMap from '../utils/createTaxonomyMap'
import baseProductFieldMapFactory from '../mappers/baseProductFieldMapFactory'
import { track as trackProductAccess } from '../utils/productAccessTracker'

// Factory, not a plain object - every other module in this store already
// follows this pattern. AppStore(registry) (app.js) constructs a fresh
// Vuex.Store on every SSR request, but this module is only evaluated once
// under a reused SSR context (runInNewContext: 'once'/false) - a plain
// object literal here would be the exact same live reference across every
// request's store (Vuex only invokes `state` as a factory when it's a
// function), leaking products/stock/in-flight-promises between requests and
// across tenant sites sharing the worker.
const state = () => ({
  products: [],
  productStock: {},
  promises: {},
  maps: {
    taxonomy: {},
    variable: {}
  },
  mapSettings: {
    'wine_product': 'wine-product',
    'vc_shop_product': 'shop-product',
    'pos_product': 'pos-product',
    'pos_food_product': 'pos-food-product',
    'pos_modifier_product': 'pos-modifier-product'
  }
})

const actions = {
  store: function({ commit }, payload) {
    commit('SET_PRODUCTS', payload)
  },
  findByID: function({ dispatch, getters }, payload) {
    const product = getters.findProductByID(payload.id)

    return (product) || dispatch('loadProductByQuery', payload)
  },
  findByType: function(store, payload) {
    return store.getters.all.filter(product => product.type === payload)
  },
  loadProductByQuery: function(store, payload) {
    try {
      return store.dispatch('queryProductByID', payload.id)
        .then((queryResponse) => {
          const productFound = queryResponse.data && queryResponse.data.hasOwnProperty('product')
          const factoryMapRequested = payload.hasOwnProperty('map') && payload.map !== ''

          if (productFound) {
            if (factoryMapRequested) {
              // applyFactoryFieldMap/generateMappedProduct expect a single
              // `product` key (see detectAndMapProduct above for the other,
              // working caller) - this used to pass `products: [...]` (an
              // array) instead, which applyFactoryFieldMap's `payload.product`
              // check never matched, silently falling through to `{}` and
              // then throwing on `.shift()`. Pre-existing, unrelated to any
              // previous fix in this file - just never exercised before.
              //
              // applyFieldMapper further down this chain calls
              // store.getters['products/getMap'](...) - a PREFIXED key,
              // because detectAndMapProduct/detectAndMapProducts (the only
              // callers that actually reached it before) always pass
              // rootGetters as `store.getters`. This action's own local
              // `store.getters` is unprefixed (Vuex namespacing), so it must
              // be wrapped the same way, or that call silently resolves to
              // undefined and throws ("... is not a function") once this
              // code path is actually reached.
              return store.dispatch('applyFactoryFieldMap', {
                product: queryResponse.data.product,
                map: payload.map,
                store: payload.store ?? { dispatch: store.dispatch, getters: store.rootGetters }
              })
                .then((product) => {
                  store.dispatch('addOrReplaceProduct', product)

                  return product
                })
            }
            else {
              store.dispatch('addOrReplaceProduct', queryResponse.data.product)
            }
          }

          return queryResponse.data.product ?? {}
        })
    }
    catch (_) {
      return {}
    }
  },
  storeProducts: function({ commit, getters }, payload) {
    commit('SET_PRODUCTS', payload)
  },
  addOrReplaceProduct: function({ commit }, payload) {
    commit('ADD_OR_REPLACE_PRODUCT', payload)
  },
  detectAndMapProduct: function({ dispatch, getters, rootGetters }, payload) {
    return dispatch('applyFactoryFieldMap', {
      product: payload,
      map: getters.findMapSetting(payload.type ?? ''),
      forceLite: payload.forceLite ?? false,
      store: {
        dispatch,
        getters: rootGetters
      }
    })
  },
  // Batched counterpart to detectAndMapProduct for hot loops over many
  // products (e.g. a full catalog listing during SSR - profiled at ~5.7s
  // for 531 products). detectAndMapProduct chains 4 nested Vuex dispatches
  // per product (applyFactoryFieldMap -> generateMappedProduct ->
  // generateFieldMap -> loadMapper), each paying dispatch's subscriber-
  // notification and promise-wrapping overhead; for hundreds of products
  // that overhead alone becomes measurable. This resolves the field map
  // once per distinct product type instead of once per product, then
  // applies it directly - same output as calling detectAndMapProduct on
  // each product individually, without the equivalent of ~4x(N-1) extra
  // dispatch round-trips for a batch of N same-typed products.
  detectAndMapProducts: function({ dispatch, getters, rootGetters }, { products, profile = false, forceLite = false }) {
    const byType = products.reduce((acc, product) => {
      const type = product.type ?? ''
      acc[type] = acc[type] || []
      acc[type].push(product)
      return acc
    }, {})
    const mapStore = { dispatch, getters: rootGetters }

    // Same taxonomy/variable maps for every product in this request
    // regardless of type, so this only needs computing once here rather
    // than once per product inside applyFieldMapper (also profiled as
    // measurable overhead across hundreds of products).
    const baseProductMap = baseProductFieldMapFactory(
      rootGetters['products/getMap']('taxonomy'),
      rootGetters['products/getMap']('variable')
    )

    // Temporary diagnostic: sums where applyFieldMapper's time actually
    // goes across the whole batch (clone vs. base-map apply vs.
    // type-specific-map apply), to find the next optimization target
    // rather than guess between them. Gated behind the same context.profile
    // flag the rest of the app's profiler uses - `timings` stays null (and
    // applyFieldMapper's Date.now() calls are skipped entirely) unless
    // profiling is actually on, so there's no per-product timer/log cost in
    // a normal production render.
    const timings = profile ? { clone: 0, baseMap: 0, typeMap: 0 } : null

    return Promise.all(
      Object.keys(byType).map(type => {
        const map = getters.findMapSetting(type)
        return dispatch('generateFieldMap', map)
          .then(fieldMap => byType[type].map(product => applyFieldMapper(fieldMap, mapStore, product, baseProductMap, timings, forceLite)))
      })
    ).then(mappedByType => {
      if (profile) {
        console.log(`[detectAndMapProducts] ${products.length} products - clone=${timings.clone}ms baseMap=${timings.baseMap}ms typeMap=${timings.typeMap}ms`)
      }
      return [].concat(...mappedByType)
    })
  },
  queryProductByID: function({ getters }, payload) {
    return getters.graphQLClient.query({
      query: PRODUCT_ID_QUERY,
      variables: { id: Number(payload) }
    })
      .catch((err) => {
        console.error(`Error querying for product ID: ${payload} - ${err.message}`)
      })
  },
  queryAndStoreProduct: function({ dispatch }, payload) {
    return dispatch('queryProductByID', payload)
      .then((queriedProduct) => dispatch('detectAndMapProduct', queriedProduct.data.product))
      .then((mappedProduct) => dispatch('addOrReplaceProduct', mappedProduct))
      .catch((err) => console.error(`Unable to query and store product: ${err.message}`))
  },
  applyFactoryFieldMap(store, payload) {
    const productSupplied = payload.hasOwnProperty('product') && typeof payload.product === 'object' && JSON.stringify(payload.product) !== '{}'
    const mapperSupplied = payload.hasOwnProperty('map')

    return (productSupplied && mapperSupplied)
      ? store.dispatch('generateMappedProduct', payload)
      : payload.product ?? {}
  },
  generateMappedProduct(store, payload) {
    return store.dispatch('generateFieldMap', payload.map)
      .then((fieldMap) => {
        return (fieldMap !== null)
          ? applyFieldMapper(fieldMap, (payload.store ?? store), payload.product, null, null, payload.forceLite ?? false)
          : payload.product ?? {}
      })
  },
  loadMapper(_, requestedMap) {
    try {
      const availableFactoryMaps = Object.keys(fieldMapFactories)

      return availableFactoryMaps.includes(requestedMap)
        ? fieldMapFactories[requestedMap]
        : ''
    }
    catch (err) {
      console.error(err.message)
      return null
    }
  },
  generateFieldMap(store, payload) {
    return store.dispatch('loadMapper', payload)
      .then((fieldMapGenerator) => {
        if (fieldMapGenerator !== null && typeof fieldMapGenerator === 'function') {
          return fieldMapGenerator(
            store.getters['getMap']('taxonomy'),
            store.getters['getMap']('variable')
          )
        }
        else {
          return {}
        }
      })
  },
  loadAllMaps: function({ dispatch }, queryResults) {
    dispatch('loadTaxonomyMap', queryResults)
    dispatch('loadVariableMap', queryResults)
  },
  loadTaxonomyMap: function({ dispatch }, payload) {
    return dispatch('storeTaxonomyMap', createTaxonomyMap(payload))
  },
  loadVariableMap: function({ dispatch }, payload) {
    return dispatch('storeVariableMap', createVariableMap(payload))
  },
  storeTaxonomyMap: function({ commit }, payload) {
    commit('SET_MAP', {
      key: 'taxonomy',
      value: payload
    })
  },
  storeVariableMap: function({ commit }, payload) {
    commit('SET_MAP', {
      key: 'variable',
      value: payload
    })
  },
  currentStock: function({ state, commit, rootState, rootGetters }, payload) {
    if (payload !== undefined && payload.force !== undefined && !payload.force) {
      if ((payload.itemid !== undefined && Object.prototype.hasOwnProperty.call(state.productStock, payload.itemid))) {
        return Promise.resolve(state.productStock[payload.itemid])
      }
    }

    let url = rootGetters.getServiceEndpoint('datastore').concat(rootState.registry.getServiceSetting('inventory', 'stock'))
    let itemid
    if (!Object.prototype.hasOwnProperty.call(state.promises, url)) {
      if (payload) {
        if (payload.itemid !== undefined) {
          itemid = payload.itemid
        }
        else if (payload.nid !== undefined) {
          itemid = payload.nid
        }
        url = url.concat('/', itemid)
      }

      if (!Object.prototype.hasOwnProperty.call(state.promises, url)) {
        state.promises[url] = fetch(url, {
          method: 'GET'
        })
          .then(resp => {
            if (resp.ok) {
              return resp.json()
            }
            throw new Error(`Error loading ProductStock: ${url} - ${resp.status} :: ${resp.statusText}`)
          })
          .then(data => data.data)
          .catch(err => {
            if (itemid) {
              applogger.error(`Unable to load product stock for Item: ${itemid} - ${err.message}`)
            }
            else {
              applogger.error(`Unable to load product stock for all Items: ${err.message}`)
            }
          })
          .then(productStock => {
            if (itemid === undefined) {
              Object.entries(productStock).forEach(([itemid, stock]) => {
                commit('UPDATE_PRODUCT_STOCK', { id: itemid, ...stock })
              })
            }
            else {
              commit('UPDATE_PRODUCT_STOCK', { id: itemid, ...productStock })
            }
          })
      }
    }

    return state.promises[url]
      .then(_ => {
        if (itemid !== undefined) {
          return state.productStock[itemid].manage_stock ? state.productStock[itemid].stock : 9999999
        }
      })
      .catch(_err => {
        return 0
      })
      .finally(_ => {
        delete state.promises[url]
      })
  }
}

const getters = {
  all(state) {
    return state.products
  },
  findMapSetting: (state) => (productType) => {
    return state.mapSettings.hasOwnProperty(productType)
      ? state.mapSettings[productType]
      : ''
  },
  graphQLClient: function(state, getters, rootState, rootGetters) {
    return rootGetters['getGraphQLClient']()
  },
  productCatalogue: (state, getters) => (catalogue = '') => {
    const filteringIsRequired = catalogue !== ''

    return filteringIsRequired
      ? getters.allProducts.filter(product => product.catalogue === catalogue)
      : getters.allProducts
  },
  findProductByID: (state, getters) => (payload) => {
    const found = getters.all.find(product => product.id === payload)
    if (found) trackProductAccess(state, found.id)
    return found
  },
  // Same lookup as findProductByID, without marking the product as
  // SSR-accessed. For callers that need to resolve many candidate products
  // just to filter/sort them down to a smaller final set (e.g. a product
  // carousel widget dereferencing a whole catalogue list before narrowing
  // to the handful it actually renders) - using the tracked getter for that
  // pass would mark every candidate as accessed and defeat page-scoped
  // pruning (PRUNE_UNREFERENCED_PRODUCTS). Callers should track the final,
  // actually-rendered items themselves (e.g. via findProductByID).
  findProductByIDUntracked: (state, getters) => (payload) => {
    return getters.all.find(product => product.id === payload)
  },
  findByRoutePath: (state, getters) => (payload) => {
    const product = getters.all.filter(product => (product.path === payload || product.path_prefixed_slash === payload))

    const found = product.length > 0
      ? product.shift()
      : null
    if (found) trackProductAccess(state, found.id)
    return found
  },
  getMap: (state) => (payload) => {
    return state.maps.hasOwnProperty(payload)
      ? state.maps[payload]
      : null
  },
  productsByItemID: function(state) {
    const result = {}
    state.products.forEach(item => { result[item.id] = item })
    // Wrapped in a Proxy so every existing bracket-access call site
    // (sellPrice, rrpPrice, basePrice, productSettings, prices, etc.)
    // gets tracked for free, without changing any of those call sites -
    // the access happens outside this getter's own body (`getters.
    // productsByItemID[itemid]`), so a plain returned object can't
    // observe which keys actually get read.
    return new Proxy(result, {
      get(target, key, receiver) {
        if (Object.prototype.hasOwnProperty.call(target, key)) {
          trackProductAccess(state, key)
        }
        return Reflect.get(target, key, receiver)
      }
    })
  },
  currentStock: function(state) {
    return (itemid) => {
      if (Object.prototype.hasOwnProperty.call(state.productStock, itemid)) {
        return state.productStock[itemid].manage_stock ? state.productStock[itemid].stock : 999999
      }
      return 9999999
    }
  },
  managedStock: function(state) {
    return (itemid) => {
      if (Object.prototype.hasOwnProperty.call(state.productStock, itemid)) {
        return !!state.productStock[itemid].manage_stock
      }
      return false
    }
  },
  productSettings: (_state, getters) => (itemid) => {
    if (Object.prototype.hasOwnProperty.call(itemid, 'id')) {
      itemid = itemid.id
    }
    if (Object.prototype.hasOwnProperty.call(getters.productsByItemID, itemid)) {
      return getters.productsByItemID[itemid].productsettings
    }
    return {}
  },
  prices: function(_state, getters) {
    return (itemid) => {
      if (Object.prototype.hasOwnProperty.call(itemid, 'id')) {
        itemid = itemid.id
      }
      if (Object.prototype.hasOwnProperty.call(getters.productsByItemID, itemid)) {
        return {
          ...getters.productsByItemID[itemid].productsettings.prices,
          price: getters.productsByItemID[itemid].productsettings.price,
          rrp: getters.rrpPrice(itemid),
          sell_price: getters.sellPrice(itemid)
        }
      }
      return {
        rrp: 0,
        price: 0
      }
    }
  },
  basePrice: function(_state, getters) {
    return (itemid) => {
      if (Object.prototype.hasOwnProperty.call(itemid, 'id')) {
        itemid = itemid.id
      }
      if (Object.prototype.hasOwnProperty.call(getters.productsByItemID, itemid)) {
        return getters.productsByItemID[itemid].productsettings.prices.base_price
      }
      applogger.error(`Product Not found (basePrice): ${itemid}`)
      return 0
    }
  },
  rrpPrice: function(_state, getters) {
    return (itemid) => {
      if (Object.prototype.hasOwnProperty.call(itemid, 'id')) {
        itemid = itemid.id
      }
      if (Object.prototype.hasOwnProperty.call(getters.productsByItemID, itemid)) {
        if (getters.productsByItemID[itemid].wineproductrrp > 0) {
          return getters.productsByItemID[itemid].wineproductrrp
        }
        // Pre-existing: productsettings has no top-level base_price (only
        // nested under .prices.base_price) - this fallback was already
        // reading an undefined path before the product_settings removal,
        // preserved as-is rather than silently changed here.
        return getters.productsByItemID[itemid].productsettings.base_price
      }
      applogger.error(`Product Not found (rrpPrice): ${itemid}`)
      return 0
    }
  },
  unitPrice: function(_state, getters, _rootState, _rootGetters) {
    return (itemid) => getters.sellPrice(itemid)
  },
  // Dereferences an item's productsettings.prices.role_adjust_prices against
  // the shared rule-definition lookup built by normalizeRoleAdjustPrices
  // (utils/normalizeRoleAdjustPrices.js) during mapping - each rule there is
  // slimmed to {rbdid, amount}, with the rest of the definition
  // (roleid/label/weight/txntype/adjustment/...) stored once under
  // adhocStorage.roleAdjustRuleDefinitions instead of repeated per product.
  // Defensive: data that never passed through applyFieldMapper (e.g.
  // somnambulist-ui's own state.products[pid] fallback) won't be in the slim
  // shape - pass it through unchanged rather than risk corrupting it.
  getResolvedRoleAdjustPrices: function(_state, _getters, _rootState, rootGetters) {
    return (item) => {
      const rules = item && item.productsettings && item.productsettings.prices
        ? item.productsettings.prices.role_adjust_prices
        : undefined

      if (!Array.isArray(rules) || rules.length === 0) {
        return rules || []
      }

      const isSlim = rules.every(rule =>
        Object.prototype.hasOwnProperty.call(rule, 'rbdid') &&
        Object.prototype.hasOwnProperty.call(rule, 'amount') &&
        Object.keys(rule).length <= 2
      )
      if (!isSlim) {
        return rules
      }

      const definitions = rootGetters.getAdHocStorageValue('roleAdjustRuleDefinitions') || {}
      return rules.map(({ rbdid, amount }) => ({ ...definitions[rbdid], rbdid, amount }))
    }
  },
  // Wraps ecommerce/getTotalPriceAdjustment (from the non-editable
  // @aspedia/vuejs-ecommerce-ui npm package, which reads
  // item.productsettings.prices.role_adjust_prices directly and has no seam
  // to intercept) so every caller gets a fully-populated rules array without
  // needing to know about the slim/shared-lookup representation. Skips
  // reconstructing the item entirely when resolution was a no-op, since one
  // call site (ecommerce-product-price.vue) runs on every catalogue card.
  getResolvedTotalPriceAdjustment: function(_state, getters, _rootState, rootGetters) {
    return (item, account, txntype) => {
      const resolvedRules = getters.getResolvedRoleAdjustPrices(item)
      const currentRules = item && item.productsettings && item.productsettings.prices
        ? item.productsettings.prices.role_adjust_prices
        : undefined
      const resolvedItem = resolvedRules === currentRules
        ? item
        : {
          ...item,
          productsettings: {
            ...item.productsettings,
            prices: {
              ...item.productsettings.prices,
              role_adjust_prices: resolvedRules
            }
          }
        }
      return rootGetters['ecommerce/getTotalPriceAdjustment'](resolvedItem, account, txntype)
    }
  },
  sellPrice: function(_state, getters, _rootState, rootGetters) {
    return (itemid, account) => {
      if (Object.prototype.hasOwnProperty.call(itemid, 'id')) {
        itemid = itemid.id
      }
      if (Object.prototype.hasOwnProperty.call(getters.productsByItemID, itemid)) {
        try {
          return getters.productsByItemID[itemid].productsettings.prices.base_price +
            getters.getResolvedTotalPriceAdjustment(getters.productsByItemID[itemid], account)
        }
        catch (err) {
          // Pre-existing: same undefined .base_price fallback as rrpPrice above.
          return getters.productsByItemID[itemid].productsettings.base_price
        }
      }
      applogger.error(`Product Not found (sellPrice): ${itemid}`)
      return 0
    }
  },
  packPrice: function(_state, getters) {
    return (itemid) => {
      if (Object.prototype.hasOwnProperty.call(itemid, 'id')) {
        itemid = itemid.id
      }
      return (getters.sellPrice(itemid) * 6)
    }
  },
  packPriceLabel: function() {
    return (itemid) => {
      return '6 Bottles.'
    }
  },
  rrpPackPrice: function(_state, getters) {
    return (itemid) => {
      if (Object.prototype.hasOwnProperty.call(itemid, 'id')) {
        itemid = itemid.id
      }
      return (getters.rrpPrice(itemid) * 6)
    }
  },
  cartMultipleHandling: function(_state, getters) {
    return (itemid) => {
      if (Object.prototype.hasOwnProperty.call(getters.productsByItemID, itemid)) {
        const value = Object.prototype.hasOwnProperty.call(getters.productsByItemID[itemid], 'cart_multiple') ? getters.productsByItemID[itemid].cart_multiple : 'sameprice'
        switch (value) {
        case '0': // The actual value of cart mutiple internally in Drupal is "0"
          return 'sameprice'
        case '1':
          return 'never'
        case '2':
          return 'always'
        default:
          return value
        }
      }
      return 'sameprice'
    }
  },
  allowInCart: function(state, getters) {
    return (itemid) => {
      if (Object.prototype.hasOwnProperty.call(itemid, 'id')) {
        itemid = itemid.id
      }
      return getters.productsByItemID[itemid].allow_in_cart
    }
  },
  productSpecials: function(_state, getters, _rootState, rootGetters) {
    return (itemid) => {
      if (Object.prototype.hasOwnProperty.call(itemid, 'id')) {
        itemid = itemid.id
      }
      const adjustments = rootGetters['ecommerce/getPriceAdjustments'](getters.productsByItemID[itemid])
      const specials = Object.values(adjustments)
        .map(special => {
          if (Object.keys(special).length > 0) {
            return { label: special.description, type: special.display }
          }
          else return {}
        })
        .filter(value => Object.keys(value).length !== 0)
      return specials
    }
  },
  hasSavings: function(_state, getters, _rootState, rootGetters) {
    return (itemid) => {
      if (Object.prototype.hasOwnProperty.call(itemid, 'id')) {
        itemid = itemid.id
      }
      return getters.basePrice(itemid) - getters.unitPrice(itemid)
    }
  },
  customQty: function(_state, getters, rootState, rootGetters) {
    return (itemid) => {
      if (Object.prototype.hasOwnProperty.call(itemid, 'id')) {
        itemid = itemid.id
      }
      const productSettings = getters['productSettings'](itemid)
      if (productSettings.customqty !== undefined) {
        const customQuantities = rootGetters['ecommerce/getConfigItem']('custom_qty', 'settings')
        const settings = Object.values(customQuantities).reduce((acc, x) => ({ ...acc, [x.qty_key]: x }), {})

        return productSettings.customqty.map(cur => {
          // Check the Custom Qty Settings format for the GraphQL Response or the RestAPI Response.
          if (typeof cur === 'string') {
            return settings[cur]
          }
          else {
            try {
              return customQuantities[cur.id]
            }
            catch (err) {
              return null
            }
          }
        }).filter(_ => _)
      }
      else {
        return []
      }
    }
  },
  maxCart: function(_state, getters) {
    return (itemid) => {
      try {
        let item = null
        if (Object.prototype.hasOwnProperty.call(itemid, 'id')) {
          item = itemid
        }
        else if (Object.prototype.hasOwnProperty.call(getters.productsByItemID, itemid)) {
          item = getters.productsByItemID[itemid]
        }

        if (Object.prototype.hasOwnProperty.call(item, 'max_cart') && item.max_cart > 0) {
          return item.max_cart
        }
        else if (Object.prototype.hasOwnProperty.call(item, 'field_n_wine_prod_max_cart') && ['string', 'number'].includes(typeof item.field_n_wine_prod_max_cart)) {
          return parseInt(item.field_n_wine_prod_max_cart)
        }
        else if (Object.prototype.hasOwnProperty.call(item, 'field_n_wine_prod_max_cart') && Array.isArray(item.field_n_wine_prod_max_cart) && !isNaN(parseInt(item.field_n_wine_prod_max_cart[0].value))) {
          return parseInt(this.item.field_n_wine_prod_max_cart[0].value)
        }
        else {
          return 999999
        }
      }
      catch (_err) {
        return 999999
      }
    }
  }
}

const mutations = {
  ADD_PRODUCT: (state, payload) => state.products.push(payload),
  ADD_OR_REPLACE_PRODUCT: (state, payload) => {
    const noProducts = state.products.length <= 0
    const indexOfProduct = state.products.findIndex(product => Number(product.id) === Number(payload.id))
    const productNotStored = indexOfProduct === -1

    if (noProducts || productNotStored) {
      state.products.push(payload)
    }
    else {
      Vue.set(state.products, indexOfProduct, payload)
    }
  },
  SET_PRODUCTS: (state, payload) => {
    state.products = payload
  },
  ADD_PRODUCTS: (state, payload) => {
    Vue.set(state, 'products', state.products.concat(payload))
  },
  SET_MAP: (state, payload) => {
    state.maps[payload.key] = payload.value
  },
  REMOVE_PRODUCT_BY_ID: (state, payload) => {
    const existingProducts = JSON.parse(JSON.stringify(state.products))
    const filteredProducts = existingProducts.filter(product => product.id === payload)

    Vue.set(state, 'products', filteredProducts)
  },
  UPDATE_PRODUCT_STOCK: function(state, payload) {
    Vue.set(state.productStock, payload.id, payload)
  }
}

export default {
  namespaced: true,
  getters,
  state,
  actions,
  mutations
}
